import React, { useState, useEffect, useMemo, useRef } from "react";
import { Button } from "../ui/components/Button";
import {
  generateReport,
  getEffectiveDateInfo,
  getPlSummary,
  PlSummaryResponse,
} from "../features/cfo/data/api/trialBalanceApi";
import {
  InlineExcelViewer,
  clearParsedReportCache,
} from "../features/cfo/presentation/components/InlineExcelViewer";
import {
  setStoredReportMonths,
  getStoredReportInfo,
  setStoredReportInfo,
  clearStoredReportInfo,
  shouldClearReportCacheOnLoad,
} from "../features/cfo/utils/reportingMonth";
import { PlSummarySection } from "../features/cfo/presentation/components/PlSummarySection";
import { formatPlAmount } from "../features/client_dashboard/presentation/utils/formatPlAmount";

function formatChangePct(pct: number | null, suffix = "%"): string {
  if (pct == null) return "No previous year data";
  const sign = pct >= 0 ? "+" : "";
  return `${sign}${pct}${suffix}`;
}

function changeVariant(pct: number | null): "success" | "error" | "neutral" {
  if (pct == null) return "neutral";
  if (pct > 0) return "success";
  if (pct < 0) return "error";
  return "neutral";
}

function plSummaryToMetrics(data: PlSummaryResponse["data"]) {
  return {
    ytdRangeSubtext: data.ytd_subtext?.trim() ?? "",
    ytdRevenue: {
      value: formatPlAmount(data.ytd_revenue),
      changeText: formatChangePct(data.ytd_revenue_change_pct),
      changeVariant: changeVariant(data.ytd_revenue_change_pct),
      subtext: "vs prior year",
    },
    ytdEbitda: {
      value: formatPlAmount(data.ytd_ebitda),
      changeText: formatChangePct(data.ytd_ebitda_change_pct),
      changeVariant: changeVariant(data.ytd_ebitda_change_pct),
      subtext: "vs prior year",
    },
    ebitdaMargin: {
      value: data.ebitda_margin != null ? `${data.ebitda_margin}%` : "—",
      changeText: formatChangePct(data.ebitda_margin_change_pp, "pp"),
      changeVariant: changeVariant(data.ebitda_margin_change_pp),
      subtext: "vs prior year",
    },
    pat: {
      value: formatPlAmount(data.pat),
      changeText: formatChangePct(data.pat_change_pct),
      changeVariant: changeVariant(data.pat_change_pct),
      subtext: "vs prior year",
    },
  };
}

const PLACEHOLDER_METRICS = plSummaryToMetrics({
  ytd_revenue: 0,
  ytd_revenue_prev_available: false,
  ytd_revenue_prev: null,
  ytd_revenue_change_pct: null,
  ytd_ebitda: 0,
  ytd_ebitda_prev_available: false,
  ytd_ebitda_prev: null,
  ytd_ebitda_change_pct: null,
  ebitda_margin: null,
  ebitda_margin_prev_available: false,
  ebitda_margin_prev: null,
  ebitda_margin_change_pp: null,
  pat: 0,
  pat_prev_available: false,
  pat_prev: null,
  pat_change_pct: null,
  ytd_subtext: "",
});

export default function ProfitAndLossPage() {
  const [startMonth, setStartMonth] = useState<string>("");
  const [endMonth, setEndMonth] = useState<string>("");
  const [isLoadingDates, setIsLoadingDates] = useState<boolean>(true);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [reportId, setReportId] = useState<number | null>(null);
  const [reportName, setReportName] = useState<string>("");
  const [plSummaryLoading, setPlSummaryLoading] = useState<boolean>(true);
  const [plSummaryData, setPlSummaryData] = useState<
    PlSummaryResponse["data"] | null
  >(null);

  const plSummaryMetrics = useMemo(
    () =>
      plSummaryData ? plSummaryToMetrics(plSummaryData) : PLACEHOLDER_METRICS,
    [plSummaryData],
  );

  // Auto-apply guards. autoApplyFiredRef ensures we only trigger Apply once
  // per page mount. handleApplyRef lets the auto-apply effect always call the
  // latest handleApply without putting it in the effect deps (its closure
  // captures startMonth/endMonth, which we want fresh at fire-time).
  const autoApplyFiredRef = useRef(false);
  const handleApplyRef = useRef<(() => Promise<void>) | null>(null);

  // Fetch P&L summary on mount
  useEffect(() => {
    let cancelled = false;
    setPlSummaryLoading(true);
    getPlSummary()
      .then((res) => {
        if (!cancelled && res.success && res.data) {
          setPlSummaryData(res.data);
        }
      })
      .catch(() => {
        if (!cancelled) setPlSummaryData(null);
      })
      .finally(() => {
        if (!cancelled) setPlSummaryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch effective date and set default months on mount
  useEffect(() => {
    const initializeMonths = async () => {
      try {
        if (shouldClearReportCacheOnLoad()) {
          clearStoredReportInfo();
          clearParsedReportCache();
        }

        // Fetch reporting period from backend (FY start → latest closed month).
        const effectiveDateInfo = await getEffectiveDateInfo();
        const { fy_start_month, latest_closed_month } = effectiveDateInfo.data;
        if (!fy_start_month || !latest_closed_month) {
          throw new Error("No closed month available to generate report.");
        }

        setStartMonth(fy_start_month);
        setEndMonth(latest_closed_month);
        setStoredReportMonths(fy_start_month, latest_closed_month);

        const storedReport = getStoredReportInfo();
        if (
          storedReport &&
          storedReport.startMonth === fy_start_month &&
          storedReport.endMonth === latest_closed_month
        ) {
          setReportId(storedReport.reportId);
          setReportName(storedReport.reportName);
        }
      } catch (err: any) {
        console.error("Failed to fetch reporting period:", err);
        setError(err?.message || "Failed to load reporting period.");
      } finally {
        setIsLoadingDates(false);
      }
    };

    initializeMonths();
  }, []);

  const getFirstDayOfMonth = (monthStr: string): string => {
    return `${monthStr}-01`;
  };

  const getLastDayOfMonth = (monthStr: string): string => {
    const [year, month] = monthStr.split("-").map(Number);
    const lastDay = new Date(year, month, 0).getDate();
    return `${monthStr}-${String(lastDay).padStart(2, "0")}`;
  };

  const handleApply = async (): Promise<void> => {
    setError("");

    if (!startMonth || !endMonth) {
      setError("Please select both start and end months.");
      return;
    }

    if (startMonth > endMonth) {
      setError("Start month must be before or equal to end month.");
      return;
    }

    setStoredReportMonths(startMonth, endMonth);
    setIsGenerating(true);
    try {
      const response = await generateReport({
        from_date: getFirstDayOfMonth(startMonth),
        to_date: getLastDayOfMonth(endMonth),
        group_by: "monthwise",
        save_report: true,
      });

      const newReportId = response.data.report_id;
      if (!newReportId) {
        throw new Error("Report ID not returned from server.");
      }

      const newReportName = response.data.report_name || "";
      setReportId(newReportId);
      setReportName(newReportName);
      setStoredReportInfo({
        startMonth,
        endMonth,
        reportId: newReportId,
        reportName: newReportName,
      });
    } catch (err: any) {
      setError(err.message || "Failed to generate report. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  // Keep a ref to the latest handleApply so the auto-apply effect can fire
  // it without needing to depend on its identity (it's not useCallback'd).
  useEffect(() => {
    handleApplyRef.current = handleApply;
  });

  // Auto-trigger Apply once per mount after the months have been initialised,
  // mirroring ReportingPageContent's autoApplyOnMount behaviour. Skipped when
  // we already restored a report for the same months from localStorage.
  useEffect(() => {
    if (autoApplyFiredRef.current) return;
    if (isLoadingDates) return;
    if (!startMonth || !endMonth) return;
    if (reportId) return;
    autoApplyFiredRef.current = true;
    const timeoutId = window.setTimeout(() => {
      handleApplyRef.current?.();
    }, 1500);
    return () => window.clearTimeout(timeoutId);
  }, [isLoadingDates, startMonth, endMonth, reportId]);

  const headerActions = (
    <>
      <div className="flex items-center gap-2">
        <label className="text-xs font-caption text-subtext-color">
          Start Month
        </label>
        <input
          type="month"
          value={startMonth}
          onChange={(e) => setStartMonth(e.target.value)}
          disabled={isLoadingDates}
          className="px-2 py-1.5 border border-solid border-neutral-border rounded-md text-sm font-body disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs font-caption text-subtext-color">
          End Month
        </label>
        <input
          type="month"
          value={endMonth}
          onChange={(e) => setEndMonth(e.target.value)}
          disabled={isLoadingDates}
          className="px-2 py-1.5 border border-solid border-neutral-border rounded-md text-sm font-body disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </div>
      <Button
        variant="brand-primary"
        onClick={handleApply}
        disabled={isGenerating || isLoadingDates || !startMonth || !endMonth}
        size="medium"
        className="h-[35px]"
      >
        {isGenerating ? "Applying..." : "Apply"}
      </Button>
    </>
  );

  const emptyState = isLoadingDates
    ? "Loading..."
    : "Select start and end months and click Apply to view the report.";

  return (
    <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-6 bg-neutral-50 px-6 py-6 overflow-auto">
      <PlSummarySection loading={plSummaryLoading} metrics={plSummaryMetrics} />

      {error && (
        <div className="w-full px-4 py-2 bg-red-50 border border-red-200 rounded-md text-body font-body text-red-700">
          {error}
        </div>
      )}

      <div
        className={`flex w-full grow shrink-0 basis-0 overflow-auto rounded-md border border-neutral-border bg-default-background ${reportId ? "min-h-[80vh]" : ""}`}
      >
        <InlineExcelViewer
          reportId={reportId}
          reportName={reportName}
          targetSheet="IS"
          headerActions={headerActions}
          showHeaderWhenEmpty
          fallbackTitle="Profit & Loss"
          isGenerating={isGenerating}
          emptyState={emptyState}
          showStatusBar={false}
        />
      </div>
    </div>
  );
}
